import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-all duration-[180ms] ease-[cubic-bezier(0.2,0,0,1)]",
  {
    variants: {
      variant: {
        // Primary: Navy fill → main CTA
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
        // Accent: Sky blue fill → highlight CTA
        accent:
          "bg-accent text-accent-foreground hover:bg-accent/90 active:bg-accent/80",
        // Outline: White with navy border
        outline:
          "border border-primary/20 bg-white text-primary hover:border-primary/40 hover:bg-primary/5 active:bg-primary/10",
        // Secondary: Light tonal
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/70",
        // Ghost: Transparent
        ghost:
          "hover:bg-secondary hover:text-foreground",
        // Link
        link:
          "text-accent underline-offset-4 hover:underline",
        // Destructive
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // For navy backgrounds: white border
        navyOutline:
          "border border-white/30 bg-transparent text-white hover:bg-white/10 hover:border-white/60",
        // For navy backgrounds: white fill
        navySolid:
          "bg-white text-primary hover:bg-white/90 active:bg-white/80",
        // Filter chip: pill-shaped, togglable (use `chip-active` class externally)
        hero:
          "border border-white/25 bg-white/10 text-white hover:bg-white/20 rounded-pill",
        // Chip: neutral filter button
        chip:
          "border border-border bg-white text-muted-foreground hover:border-primary/30 hover:text-primary rounded-pill",
        // Chip active
        chipActive:
          "border border-primary bg-primary text-primary-foreground rounded-pill",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-11 px-7",
        xl: "h-12 px-9 text-base",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
